import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Ticket, CheckCircle2, Loader2, ArrowRight, TicketX, Check, Crown } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURES = [
  'Neomezený přístup ke všemu obsahu',
  'Exkluzivní epizody od tvůrců',
  'HD video i audio kvalita',
  'Bez reklam',
  'Podpora oblíbených tvůrců',
];

export function RedeemCodeModal({ open, onOpenChange }) {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);   // successful redeem
  const [exhausted, setExhausted] = useState(false); // code hit 20/20

  const handleRedeem = async (e) => {
    e.preventDefault();
    if (!code.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(
        `${API}/video-codes/redeem`,
        { code: code.trim().toUpperCase() },
        { headers: { Authorization: `Bearer ${accessToken}` } }
      );
      setResult(res.data);
      if (res.data.already_had_access) {
        toast.info('Přístup k této epizodě již máte');
      } else {
        toast.success('Kód uplatněn! Přístup k epizodě odemčen.');
      }
    } catch (err) {
      const status = err.response?.status;
      if (status === 409) {
        // Code is exhausted — show paywall upsell instead of a plain toast
        setExhausted(true);
      } else {
        toast.error(err.response?.data?.detail || 'Neplatný nebo již použitý kód');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleWatch = () => {
    if (!result) return;
    onOpenChange(false);
    if (result.media_type === 'video') {
      navigate(`/watch/${result.episode_id}`);
    } else {
      navigate(`/episode/${result.episode_id}`);
    }
  };

  const handleClose = () => {
    setCode('');
    setResult(null);
    setExhausted(false);
    onOpenChange(false);
  };

  const handleGoSubscribe = () => {
    handleClose();
    navigate('/subscribe');
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md bg-card border-border">

        {/* ── Exhausted: paywall upsell ── */}
        {exhausted ? (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <TicketX className="w-5 h-5" />
                Kód je vyčerpán
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                Bohužel tento kód byl již využit na maximum. Ale nevadí —
                členstvím získáš přístup ke všemu obsahu ihned.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-2 space-y-4">
              {/* Feature list */}
              <div className="rounded-xl border border-border bg-secondary/30 p-4 space-y-2">
                {FEATURES.map((f) => (
                  <div key={f} className="flex items-center gap-2.5 text-sm">
                    <Check className="w-4 h-4 text-primary flex-shrink-0" />
                    <span>{f}</span>
                  </div>
                ))}
              </div>

              <Button
                onClick={handleGoSubscribe}
                className="w-full bg-primary hover:bg-primary/90 font-semibold"
              >
                <Crown className="w-4 h-4 mr-2" />
                Koupit členství a sledovat hned
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>

              <button
                onClick={() => setExhausted(false)}
                className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                ← Zadat jiný kód
              </button>
            </div>
          </>
        ) : !result ? (
          /* ── Default: code input form ── */
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Ticket className="w-5 h-5 text-primary" />
                Uplatnit kód
              </DialogTitle>
              <DialogDescription>
                Zadejte kód který jste dostali od tvůrce pro přístup k epizodě zdarma.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={handleRedeem} className="space-y-4 mt-2">
              <div>
                <Label className="text-sm text-muted-foreground mb-1.5 block">Kód</Label>
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="A3F7X2K9"
                  className="font-mono text-lg tracking-widest text-center uppercase"
                  maxLength={16}
                  autoFocus
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !code.trim()}
                className="w-full bg-primary hover:bg-primary/90"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Ticket className="w-4 h-4 mr-2" />
                    Uplatnit kód
                  </>
                )}
              </Button>
            </form>
          </>
        ) : (
          /* ── Success: go to episode ── */
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Ticket className="w-5 h-5 text-primary" />
                Uplatnit kód
              </DialogTitle>
            </DialogHeader>

            <div className="mt-2 space-y-4">
              <div className="flex items-center gap-3 p-4 rounded-lg bg-green-500/10 border border-green-500/30">
                <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-green-500">Kód uplatněn</p>
                  <p className="text-sm text-muted-foreground mt-0.5">{result.episode_title}</p>
                </div>
              </div>
              <Button onClick={handleWatch} className="w-full bg-primary hover:bg-primary/90">
                Přejít na epizodu
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </>
        )}

      </DialogContent>
    </Dialog>
  );
}
